"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodesCryptoService = void 0;
const ttag_1 = require("ttag");
const crypto_1 = require("../../crypto");
const interface_1 = require("../../interface");
const errors_1 = require("../../errors");
const errors_2 = require("../errors");
const uids_1 = require("../uids");
/**
 * Provides crypto operations for nodes metadata.
 *
 * The node crypto service is responsible for decrypting and encrypting node
 * metadata. It should export high-level actions only, such as "decrypt node"
 * instead of low-level operations like "decrypt node key". Low-level operations
 * should be kept private to the module.
 *
 * The service owns the logic to switch between old and new crypto model.
 */
class NodesCryptoService {
    telemetry;
    driveCrypto;
    account;
    shareService;
    logger;
    reportedDecryptionErrors = new Set();
    reportedVerificationErrors = new Set();
    constructor(telemetry, driveCrypto, account, shareService) {
        this.telemetry = telemetry;
        this.driveCrypto = driveCrypto;
        this.account = account;
        this.shareService = shareService;
        this.telemetry = telemetry;
        this.logger = telemetry.getLogger('nodes-crypto');
        this.driveCrypto = driveCrypto;
        this.account = account;
        this.shareService = shareService;
    }
    async decryptNode(node, parentKey) {
        const commonNodeMetadata = {
            ...node,
            encryptedCrypto: undefined,
        };
        const signatureEmailKeys = node.encryptedCrypto.signatureEmail
            ? await this.account.getPublicKeys(node.encryptedCrypto.signatureEmail)
            : [];
        // Parent key is node or share key. Anonymous files are signed with
        // the node parent key. If the anonymous file is shared directly,
        // there is no access to the parent key. In that case, the verification
        // is skipped.
        const nodeParentKeys = node.parentUid ? [parentKey] : [];
        // Anonymous uploads (without signature email set) use parent key instead.
        const keyVerificationKeys = node.encryptedCrypto.signatureEmail ? signatureEmailKeys : nodeParentKeys;
        let nameVerificationKeys;
        const nameSignatureEmail = node.encryptedCrypto.nameSignatureEmail;
        if (nameSignatureEmail === node.encryptedCrypto.signatureEmail) {
            nameVerificationKeys = keyVerificationKeys;
        }
        else {
            nameVerificationKeys = nameSignatureEmail
                ? await this.account.getPublicKeys(nameSignatureEmail)
                : nodeParentKeys;
        }
        const { name, author: nameAuthor } = await this.decryptName(node, parentKey, nameVerificationKeys);
        let passphrase, key, passphraseSessionKey, keyAuthor;
        try {
            const keyResult = await this.decryptKey(node, parentKey, keyVerificationKeys);
            passphrase = keyResult.passphrase;
            key = keyResult.key;
            passphraseSessionKey = keyResult.passphraseSessionKey;
            keyAuthor = keyResult.author;
        }
        catch (error) {
            void this.reportDecryptionError(node, 'nodeKey', error);
            const message = (0, errors_2.getErrorMessage)(error);
            const errorMessage = (0, ttag_1.c)('Error').t `Failed to decrypt node key: ${message}`;
            return {
                node: {
                    ...commonNodeMetadata,
                    name,
                    keyAuthor: (0, interface_1.resultError)({
                        claimedAuthor: node.encryptedCrypto.signatureEmail,
                        error: errorMessage,
                    }),
                    nameAuthor,
                    activeRevision: 'file' in node.encryptedCrypto ? (0, interface_1.resultError)(new Error(errorMessage)) : undefined,
                    folder: undefined,
                    errors: [error],
                },
            };
        }
        const errors = [];
        let hashKey;
        let hashKeyAuthor;
        let folder;
        let folderExtendedAttributesAuthor;
        if ('folder' in node.encryptedCrypto) {
            try {
                const hashKeyResult = await this.decryptHashKey(node, key, signatureEmailKeys);
                hashKey = hashKeyResult.hashKey;
                hashKeyAuthor = hashKeyResult.author;
            }
            catch (error) {
                void this.reportDecryptionError(node, 'nodeHashKey', error);
                errors.push(error);
            }
            try {
                const folderExtendedAttributesVerificationKeys = node.encryptedCrypto.signatureEmail
                    ? signatureEmailKeys
                    : [key];
                const extendedAttributesResult = await this.decryptExtendedAttributes(node, node.encryptedCrypto.folder.armoredExtendedAttributes, key, folderExtendedAttributesVerificationKeys, node.encryptedCrypto.signatureEmail);
                folder = {
                    extendedAttributes: extendedAttributesResult.extendedAttributes,
                };
                folderExtendedAttributesAuthor = extendedAttributesResult.author;
            }
            catch (error) {
                void this.reportDecryptionError(node, 'nodeExtendedAttributes', error);
                errors.push(error);
            }
        }
        let activeRevision;
        let contentKeyPacketSessionKey;
        let contentKeyPacketAuthor;
        if ('file' in node.encryptedCrypto) {
            try {
                activeRevision = (0, interface_1.resultOk)(await this.decryptRevision(node.uid, node.encryptedCrypto.activeRevision, key));
            }
            catch (error) {
                void this.reportDecryptionError(node, 'nodeExtendedAttributes', error);
                const message = (0, errors_2.getErrorMessage)(error);
                const errorMessage = (0, ttag_1.c)('Error').t `Failed to decrypt active revision: ${message}`;
                activeRevision = (0, interface_1.resultError)(new Error(errorMessage));
            }
            try {
                const keySessionKeyResult = await this.driveCrypto.decryptAndVerifySessionKey(node.encryptedCrypto.file.base64ContentKeyPacket, node.encryptedCrypto.file.armoredContentKeyPacketSignature, key, 
                // Content key packet is signed with the node key, but
                // in the past some clients signed with the address key.
                [key, ...keyVerificationKeys]);
                contentKeyPacketSessionKey = keySessionKeyResult.sessionKey;
                contentKeyPacketAuthor =
                    keySessionKeyResult.verified &&
                        (await this.handleClaimedAuthor(node, 'nodeContentKey', (0, ttag_1.c)('Property').t `content key`, keySessionKeyResult.verified, node.encryptedCrypto.signatureEmail));
            }
            catch (error) {
                void this.reportDecryptionError(node, 'nodeContentKey', error);
                const message = (0, errors_2.getErrorMessage)(error);
                const errorMessage = (0, ttag_1.c)('Error').t `Failed to decrypt content key: ${message}`;
                contentKeyPacketAuthor = (0, interface_1.resultError)({
                    claimedAuthor: node.encryptedCrypto.signatureEmail,
                    error: errorMessage,
                });
                errors.push(error);
            }
        }
        // If key signature verificaiton failed, prefer returning error from
        // the key directly. If key signature is ok but not hash or folder
        // extended attributes, return that error instead. Only if all the
        // signatures using the same signature email are ok, return OK.
        let finalKeyAuthor;
        if (!keyAuthor.ok) {
            finalKeyAuthor = keyAuthor;
        }
        if (!finalKeyAuthor && contentKeyPacketAuthor && !contentKeyPacketAuthor.ok) {
            finalKeyAuthor = contentKeyPacketAuthor;
        }
        if (!finalKeyAuthor && hashKeyAuthor && !hashKeyAuthor.ok) {
            finalKeyAuthor = hashKeyAuthor;
        }
        if (!finalKeyAuthor && folderExtendedAttributesAuthor && !folderExtendedAttributesAuthor.ok) {
            finalKeyAuthor = folderExtendedAttributesAuthor;
        }
        if (!finalKeyAuthor) {
            finalKeyAuthor = keyAuthor;
        }
        return {
            node: {
                ...commonNodeMetadata,
                name,
                keyAuthor: finalKeyAuthor,
                nameAuthor,
                activeRevision,
                folder,
                errors: errors.length ? errors : undefined,
            },
            keys: {
                passphrase,
                key,
                passphraseSessionKey,
                contentKeyPacketSessionKey,
                hashKey,
            },
        };
    }
    async decryptKey(node, parentKey, verificationKeys) {
        const key = await this.driveCrypto.decryptKey(node.encryptedCrypto.armoredKey, node.encryptedCrypto.armoredNodePassphrase, node.encryptedCrypto.armoredNodePassphraseSignature, [parentKey], verificationKeys);
        return {
            passphrase: key.passphrase,
            key: key.key,
            passphraseSessionKey: key.passphraseSessionKey,
            author: await this.handleClaimedAuthor(node, 'nodeKey', (0, ttag_1.c)('Property').t `key`, key.verified, node.encryptedCrypto.signatureEmail, verificationKeys.length === 0),
        };
    }
    async decryptName(node, parentKey, verificationKeys) {
        const nameSignatureEmail = node.encryptedCrypto.nameSignatureEmail;
        try {
            const { name, verified } = await this.driveCrypto.decryptNodeName(node.encryptedName, parentKey, verificationKeys);
            return {
                name: (0, interface_1.resultOk)(name),
                author: await this.handleClaimedAuthor(node, 'nodeName', (0, ttag_1.c)('Property').t `name`, verified, nameSignatureEmail, verificationKeys.length === 0),
            };
        }
        catch (error) {
            void this.reportDecryptionError(node, 'nodeName', error);
            const errorMessage = (0, errors_2.getErrorMessage)(error);
            return {
                name: (0, interface_1.resultError)(new Error(errorMessage)),
                author: (0, interface_1.resultError)({
                    claimedAuthor: nameSignatureEmail,
                    error: errorMessage,
                }),
            };
        }
    }
    async getNameSessionKey(node, parentKey) {
        return this.driveCrypto.decryptSessionKey(node.encryptedName, parentKey);
    }
    async decryptHashKey(node, nodeKey, addressKeys) {
        if (!('folder' in node.encryptedCrypto)) {
            // This is developer error.
            throw new Error('Node is not a folder');
        }
        const { hashKey, verified } = await this.driveCrypto.decryptNodeHashKey(node.encryptedCrypto.folder.armoredHashKey, nodeKey, addressKeys);
        return {
            hashKey,
            author: await this.handleClaimedAuthor(node, 'nodeHashKey', (0, ttag_1.c)('Property').t `hash key`, verified, node.encryptedCrypto.signatureEmail),
        };
    }
    async decryptRevision(nodeUid, encryptedRevision, nodeKey) {
        const verificationKeys = encryptedRevision.signatureEmail
            ? await this.account.getPublicKeys(encryptedRevision.signatureEmail)
            : [nodeKey];
        const { extendedAttributes, author: contentAuthor } = await this.decryptExtendedAttributes({ uid: nodeUid, creationTime: encryptedRevision.creationTime }, encryptedRevision.armoredExtendedAttributes, nodeKey, verificationKeys, encryptedRevision.signatureEmail);
        return {
            uid: encryptedRevision.uid,
            state: encryptedRevision.state,
            creationTime: encryptedRevision.creationTime,
            storageSize: encryptedRevision.storageSize,
            contentAuthor,
            extendedAttributes,
            thumbnails: encryptedRevision.thumbnails,
        };
    }
    async decryptExtendedAttributes(node, encryptedExtendedAttributes, nodeKey, addressKeys, signatureEmail) {
        if (!encryptedExtendedAttributes) {
            return {
                author: (0, interface_1.resultOk)(signatureEmail),
            };
        }
        const { extendedAttributes, verified } = await this.driveCrypto.decryptExtendedAttributes(encryptedExtendedAttributes, nodeKey, addressKeys);
        return {
            extendedAttributes,
            author: await this.handleClaimedAuthor(node, 'nodeExtendedAttributes', (0, ttag_1.c)('Property').t `attributes`, verified, signatureEmail),
        };
    }
    async createFolder(parentKeys, address, name, extendedAttributes) {
        const { email, addressKey } = address;
        const [nodeKeys, { armoredNodeName }, hash] = await Promise.all([
            this.driveCrypto.generateKey([parentKeys.key], addressKey),
            this.driveCrypto.encryptNodeName(name, undefined, parentKeys.key, addressKey),
            this.driveCrypto.generateLookupHash(name, parentKeys.hashKey),
        ]);
        const { armoredHashKey, hashKey } = await this.driveCrypto.generateHashKey(nodeKeys.decrypted.key);
        const { armoredExtendedAttributes } = extendedAttributes
            ? await this.driveCrypto.encryptExtendedAttributes(extendedAttributes, nodeKeys.decrypted.key, addressKey)
            : { armoredExtendedAttributes: undefined };
        return {
            encryptedCrypto: {
                encryptedName: armoredNodeName,
                hash,
                armoredKey: nodeKeys.encrypted.armoredKey,
                armoredNodePassphrase: nodeKeys.encrypted.armoredPassphrase,
                armoredNodePassphraseSignature: nodeKeys.encrypted.armoredPassphraseSignature,
                folder: {
                    armoredExtendedAttributes: armoredExtendedAttributes,
                    armoredHashKey,
                },
                signatureEmail: email,
                nameSignatureEmail: email,
            },
            keys: {
                passphrase: nodeKeys.decrypted.passphrase,
                key: nodeKeys.decrypted.key,
                passphraseSessionKey: nodeKeys.decrypted.passphraseSessionKey,
                hashKey,
            },
        };
    }
    async encryptNewName(parentKeys, nodeNameSessionKey, address, newName) {
        const { email, addressKey } = address;
        const { armoredNodeName } = await this.driveCrypto.encryptNodeName(newName, nodeNameSessionKey, parentKeys.key, addressKey);
        const hash = parentKeys.hashKey
            ? await this.driveCrypto.generateLookupHash(newName, parentKeys.hashKey)
            : undefined;
        return {
            signatureEmail: email,
            armoredNodeName,
            hash,
        };
    }
    async moveNode(node, keys, parentKeys, address) {
        if (!parentKeys.hashKey) {
            throw new errors_1.ValidationError('Moving item to a non-folder is not allowed');
        }
        if (!node.name.ok) {
            throw new errors_1.ValidationError('Cannot move item without a valid name, please rename the item first');
        }
        const { email, addressKey } = address;
        const { armoredNodeName } = await this.driveCrypto.encryptNodeName(node.name.value, keys.nameSessionKey, parentKeys.key, addressKey);
        const hash = await this.driveCrypto.generateLookupHash(node.name.value, parentKeys.hashKey);
        const { armoredPassphrase, armoredPassphraseSignature } = await this.driveCrypto.encryptPassphrase(keys.passphrase, keys.passphraseSessionKey, [parentKeys.key], addressKey);
        return {
            encryptedName: armoredNodeName,
            hash,
            armoredNodePassphrase: armoredPassphrase,
            armoredNodePassphraseSignature: armoredPassphraseSignature,
            signatureEmail: email,
            nameSignatureEmail: email,
        };
    }
    async handleClaimedAuthor(node, field, signatureType, verified, claimedAuthor, notAvailableVerificationKeys = false) {
        const author = handleClaimedAuthor(signatureType, verified, claimedAuthor, notAvailableVerificationKeys);
        if (!author.ok) {
            void this.reportVerificationError(node, field, claimedAuthor);
        }
        return author;
    }
    async reportVerificationError(node, field, claimedAuthor) {
        if (this.reportedVerificationErrors.has(node.uid)) {
            return;
        }
        const fromBefore2024 = node.creationTime < new Date('2024-01-01');
        let addressMatchingDefaultShare, volumeType;
        try {
            const { volumeId } = (0, uids_1.splitNodeUid)(node.uid);
            const { email } = await this.shareService.getMyFilesShareMemberEmailKey();
            addressMatchingDefaultShare = claimedAuthor ? claimedAuthor === email : undefined;
            volumeType = await this.shareService.getVolumeMetricContext(volumeId);
        }
        catch (error) {
            this.logger.error('Failed to check if claimed author matches default share', error);
        }
        this.logger.warn(`Failed to verify ${field} for node ${node.uid} (from before 2024: ${fromBefore2024}, matching address: ${addressMatchingDefaultShare})`);
        this.telemetry.logEvent({
            eventName: 'verificationError',
            volumeType,
            field,
            addressMatchingDefaultShare,
            fromBefore2024,
        });
        this.reportedVerificationErrors.add(node.uid);
    }
    async reportDecryptionError(node, field, error) {
        if (this.reportedDecryptionErrors.has(node.uid)) {
            return;
        }
        const fromBefore2024 = node.creationTime < new Date('2024-01-01');
        let volumeType;
        try {
            const { volumeId } = (0, uids_1.splitNodeUid)(node.uid);
            volumeType = await this.shareService.getVolumeMetricContext(volumeId);
        }
        catch (error) {
            this.logger.error('Failed to get metric context', error);
        }
        this.logger.error(`Failed to decrypt node ${node.uid} (from before 2024: ${fromBefore2024})`, error);
        this.telemetry.logEvent({
            eventName: 'decryptionError',
            volumeType,
            field,
            fromBefore2024,
            error,
        });
        this.reportedDecryptionErrors.add(node.uid);
    }
}
exports.NodesCryptoService = NodesCryptoService;
/**
 * @param signatureType - Must be translated before calling this function.
 */
function handleClaimedAuthor(signatureType, verified, claimedAuthor, notAvailableVerificationKeys = false) {
    if (!claimedAuthor && notAvailableVerificationKeys) {
        return (0, interface_1.resultOk)(null);
    }
    if (verified === crypto_1.VERIFICATION_STATUS.SIGNED_AND_VALID) {
        return (0, interface_1.resultOk)(claimedAuthor || null);
    }
    return (0, interface_1.resultError)({
        claimedAuthor: claimedAuthor,
        error: (0, errors_2.getVerificationMessage)(verified, signatureType, notAvailableVerificationKeys),
    });
}
//# sourceMappingURL=cryptoService.js.map